import { locale } from "./local";

type Dateable = Date | null | number | string | undefined;

const fixDate = (date: Dateable) => {
	if (typeof date === "string") {
		const s = date.trim().replace(" ", "T").replace("+00", "+00:00");
		return s || date;
	}
	return date;
};

/** Returns UTC date string YYYY-MM-DD for a given date (canonical for comparisons/storage). */
export const toIsoDateString = (date: Dateable): string => {
	const fixed = fixDate(date);
	if (!fixed) {
		return "";
	}
	const d = new Date(fixed);
	return d.toISOString().split("T")[0]!;
};

/** Parses a YYYY-MM-DD string and returns a Date at UTC midnight (start of that day in UTC). */
export const fromIsoDateString = (isoDateStr: string): Date => {
	const trimmed = isoDateStr.slice(0, 10);
	return new Date(trimmed + "T00:00:00Z");
};

export const ageOnDate = (dob: null | string | undefined, on: Date): null | number => {
	if (!dob) {
		return null;
	}
	const born = fromIsoDateString(dob);
	if (Number.isNaN(born.getTime())) {
		return null;
	}
	const years = on.getUTCFullYear() - born.getUTCFullYear();
	const beforeBirthday = on.getUTCMonth() < born.getUTCMonth()
		|| (on.getUTCMonth() === born.getUTCMonth() && on.getUTCDate() < born.getUTCDate());
	return beforeBirthday ? years - 1 : years;
};

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;

const shortSpan = (ms: number) => {
	if (ms >= DAY) {
		return `${Math.round(ms / DAY)}d`;
	}
	if (ms >= HOUR) {
		return `${Math.round(ms / HOUR)}h`;
	}
	return `${Math.max(1, Math.round(ms / MINUTE))}m`;
};

/** Compact elapsed time, e.g. "5m ago", "3h ago", "2d ago". */
export const formatShortAgo = (date: Dateable) => {
	const fixed = fixDate(date);
	if (!fixed) {
		return "--";
	}
	const dt = new Date(fixed);
	if (Number.isNaN(dt.getTime())) {
		return "--";
	}
	const ms = Date.now() - dt.getTime();
	return ms < MINUTE ? "just now" : `${shortSpan(ms)} ago`;
};

/** Compact remaining time, e.g. "5m", "3h", "2d". */
export const formatShortUntil = (date: Dateable) => {
	const fixed = fixDate(date);
	if (!fixed) {
		return "--";
	}
	const dt = new Date(fixed);
	if (Number.isNaN(dt.getTime())) {
		return "--";
	}
	const ms = dt.getTime() - Date.now();
	return ms < MINUTE ? "moments" : shortSpan(ms);
};

export const formatToMinute = (date: Dateable, zone?: string) => {
	const fixed = fixDate(date);
	if (!fixed) {
		return "--";
	}
	const dt = new Date(fixed);
	if (Number.isNaN(dt.getTime())) {
		return "--";
	}
	const options: Intl.DateTimeFormatOptions = {
		day: "numeric",
		hour: "numeric",
		hour12: true,
		minute: "2-digit",
		month: "short",
		year: "numeric",
	};
	if (zone) {
		options.timeZone = zone;
	}
	return new Intl.DateTimeFormat(locale, options).format(dt);
};


export const formatTimeToMinute = (date: Dateable, zone?: string) => {
	const fixed = fixDate(date);
	if (!fixed) {
		return "--";
	}
	const dt = new Date(fixed);
	const options: Intl.DateTimeFormatOptions = {
		hour: "numeric",
		hour12: false,
		minute: "2-digit",
		timeZoneName: "short",
	};
	if (zone) {
		options.timeZone = zone;
	}
	return new Intl.DateTimeFormat(locale, options).format(dt);
};

export const formatTimeToSecond = (date: Dateable, zone?: string) => {
	const fixed = fixDate(date);
	if (!fixed) {
		return "--";
	}
	const dt = new Date(fixed);
	const options: Intl.DateTimeFormatOptions = {
		hour: "numeric",
		hour12: true,
		minute: "2-digit",
		second: "2-digit",
	};
	if (zone) {
		options.timeZone = zone;
	}
	return new Intl.DateTimeFormat(locale, options).format(dt);
};


export const formatToDay = (date: Dateable, zone?: string) => {
	const fixed = fixDate(date);
	if (!fixed) {
		return "--";
	}
	const dt = new Date(fixed);
	if (Number.isNaN(dt.getTime())) {
		return "--";
	}
	const options: Intl.DateTimeFormatOptions = {
		day: "numeric",
		month: "short",
		year: "numeric",
	};
	if (zone) {
		options.timeZone = zone;
	}
	return new Intl.DateTimeFormat(locale, options).format(dt);
};
export const formatDateFullMonth = (date: Dateable, zone?: string) => {
	const fixed = fixDate(date);
	if (!fixed) {
		return "--";
	}
	const dt = new Date(fixed);
	const options: Intl.DateTimeFormatOptions = {
		day: "numeric",
		month: "long",
		year: "numeric",
	};
	if (zone) {
		options.timeZone = zone;
	}
	return new Intl.DateTimeFormat(locale, options).format(dt);
};
export const formatToDayNumeric = (date: Dateable, zone?: string) => {
	const fixed = fixDate(date);
	if (!fixed) {
		return "--";
	}
	const dt = new Date(fixed);
	const options: Intl.DateTimeFormatOptions = {
		day: "numeric",
		month: "numeric",
		year: "numeric",
	};
	if (zone) {
		options.timeZone = zone;
	}
	return new Intl.DateTimeFormat(locale, options).format(dt);
};

export const formatDayMonth = (date: Dateable, zone?: string) => {
	const fixed = fixDate(date);
	if (!fixed) {
		return "--";
	}
	const dt = new Date(fixed);
	const options: Intl.DateTimeFormatOptions = {
		day: "numeric",
		month: "short",
	};
	if (zone) {
		options.timeZone = zone;
	}
	return new Intl.DateTimeFormat(locale, options).format(dt);
};

/** The same day and month `formatDayMonth` renders, split so a caller can stack them — the flight
 *  card's date chip puts the day in a circle with the month beneath it. `undefined` when the date
 *  is missing or unparseable, so the caller can drop the chip rather than render an empty one. */
export const formatDayMonthParts = (date: Dateable, zone?: string): undefined | { day: string; month: string } => {
	const fixed = fixDate(date);
	if (!fixed) {
		return undefined;
	}
	const dt = new Date(fixed);
	if (Number.isNaN(dt.getTime())) {
		return undefined;
	}
	const options: Intl.DateTimeFormatOptions = zone ? { timeZone: zone } : {};
	return {
		day: new Intl.DateTimeFormat(locale, { ...options, day: "numeric" }).format(dt),
		month: new Intl.DateTimeFormat(locale, { ...options, month: "short" }).format(dt),
	};
};

export const formatShortMonth = (date: Dateable, zone?: string) => {
	const fixed = fixDate(date);
	if (!fixed) {
		return "--";
	}
	const dt = new Date(fixed);
	const options: Intl.DateTimeFormatOptions = { month: "short" };
	if (zone) {
		options.timeZone = zone;
	}
	return new Intl.DateTimeFormat(locale, options).format(dt);
};

export const formatMonthYear = (date: Dateable, zone?: string) => {
	const fixed = fixDate(date);
	if (!fixed) {
		return "--";
	}
	const dt = new Date(fixed);
	const options: Intl.DateTimeFormatOptions = {
		year: "numeric",
		month: "long",
	};
	if (zone) {
		options.timeZone = zone;
	}
	return new Intl.DateTimeFormat(locale, options).format(dt);
};

/** A span as `H:MM`; the caller owns the label. `undefined`, never a placeholder, so the caller
 *  can omit the row rather than print `0:00` under a label a pilot reads as measured. */
export const formatHourMinuteSpan = (start: Dateable, end: Dateable): string | undefined => {
	const fixedStart = fixDate(start);
	const fixedEnd = fixDate(end);
	if (!fixedStart || !fixedEnd) {
		return undefined;
	}
	const startMs = new Date(fixedStart).getTime();
	const endMs = new Date(fixedEnd).getTime();
	if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
		return undefined;
	}
	const minutes = Math.floor((endMs - startMs) / 60_000);
	if (minutes <= 0) {
		return undefined;
	}
	return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
};

/**
 * Formats the duration between two dates in hours and minutes
 * @param startDate The start date
 * @param endDate The end date
 * @param fallback Optional fallback string if either date is invalid (defaults to "--")
 * @returns Formatted duration string (e.g. "2h 30m" or "45m")
 */
export const formatDuration = (
	startDate: Dateable,
	endDate: Dateable,
	fallback: string = "--",
): string => {
	const fixedStart = fixDate(startDate);
	const fixedEnd = fixDate(endDate);
	
	if (!fixedStart || !fixedEnd) {
		return fallback;
	}
	
	const start = new Date(fixedStart);
	const end = new Date(fixedEnd);
	
	// Calculate difference in milliseconds
	const diffMs = Math.abs(end.getTime() - start.getTime());

	
	// Convert to hours and minutes
	const hours = Math.floor(diffMs / (1000 * 60 * 60));
	const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
	
	// Format the output
	if (hours === 0) {
		return `${minutes}m`;
	} else {
		return `${hours}h ${minutes}m`;
	}
};

